import { GameState, MoveType, EffectType } from '../../types';
import { MoveInfo } from '../Core/CommonAIUtils';
import { AstraRules, copyState, moveKey, stateKey, describeMove } from './AstraRules';
import { evaluate, terminalScore, MATE, PIECE_VALUES } from './AstraEvaluator';

export type Bound = 'exact' | 'lower' | 'upper';
interface Entry {depth:number; score:number; bound:Bound; best?:string; pv:MoveInfo[]}
interface Line {score:number; pv:MoveInfo[]}
export interface RootScore {description:string; score:number; bound:Bound}
export interface SearchResult extends Line {
  move:MoveInfo; depth:number; nodes:number; qnodes:number; cutoffs:number; ttHits:number;
  elapsedMs:number; timedOut:boolean; rootScores:RootScore[]; iterations:Array<{depth:number;score:number;nodes:number;ms:number}>;
}
class SearchTimeout extends Error {}

export class AstraSearch {
  private rules = new AstraRules();
  private table = new Map<string, Entry>();
  private killers = new Map<number,string[]>();
  private history = new Map<string,number>();
  private deadline=0;
  private rootLight=true;
  private nodes=0;
  private qnodes=0;
  private cutoffs=0;
  private ttHits=0;
  private maxDepth=12;

  constructor(options: {maxDepth?:number} = {}) { this.maxDepth=options.maxDepth ?? 12; }
  private checkTime(): void { if (Date.now() >= this.deadline) throw new SearchTimeout(); }
  private staticScore(state:GameState):number { return evaluate(state).total*(this.rootLight?1:-1); }

  findBestMove(input:GameState, budgetMs:number):SearchResult {
    const started=Date.now();
    this.deadline=started+Math.max(1,budgetMs*0.93);
    this.rootLight=input.isLightTurn;
    this.nodes=0;this.qnodes=0;this.cutoffs=0;this.ttHits=0;
    this.table.clear();this.killers.clear();this.history.clear();
    const state=copyState(input);
    const moves=this.rules.legalMoves(state);
    if (!moves.length) throw new Error('ZSTR has no legal move');
    const ordered=this.order(state,moves,undefined,0);
    let best:Line={score:this.staticScore(state),pv:[ordered[0]]};
    let completed=0; let timedOut=false; let rootScores:RootScore[]=[];
    const iterations:SearchResult['iterations']=[];
    for(let depth=1;depth<=this.maxDepth;depth++) {
      try {
        let alpha=-Infinity;
        let candidate:Line={score:-Infinity,pv:[]};
        const scores:RootScore[]=[];
        for(const move of this.order(state,moves,moveKey(best.pv[0]),0)) {
          this.checkTime();
          const beforeAlpha=alpha;
          const line=this.searchMove(state,move,depth-1,alpha,Infinity,1,0);
          if (!line) continue;
          const bound:Bound=line.score<=beforeAlpha?'upper':'exact';
          scores.push({description:describeMove(state,move),score:line.score,bound});
          if(line.score>candidate.score) candidate={score:line.score,pv:[move,...line.pv]};
          alpha=Math.max(alpha,line.score);
          // If even depth one cannot finish, retain the best actually searched move.
          if(completed===0) {best=candidate;rootScores=[...scores];}
          if(candidate.score>=MATE-1) break;
        }
        this.checkTime();
        if(candidate.pv.length) {
          best=candidate;completed=depth;rootScores=scores.sort((a,b)=>b.score-a.score);
          iterations.push({depth,score:best.score,nodes:this.nodes,ms:Date.now()-started});
        }
        if(best.score>=MATE-64 || Date.now()-started>budgetMs*0.65) break;
      } catch(e) { if(!(e instanceof SearchTimeout)) throw e;timedOut=true;break; }
    }
    return {...best,move:best.pv[0],depth:completed,nodes:this.nodes,qnodes:this.qnodes,cutoffs:this.cutoffs,
      ttHits:this.ttHits,elapsedMs:Date.now()-started,timedOut,rootScores,iterations};
  }

  private searchMove(state:GameState,move:MoveInfo,depth:number,alpha:number,beta:number,ply:number,qleft:number,chain=0):Line|null {
    this.checkTime();
    const outcomes=this.rules.outcomes(state,move);
    if(!outcomes.length) return null;
    let score=0;let representative:MoveInfo[]=[];let largest=-1;
    for(const outcome of outcomes) {
      this.checkTime();
      // Chance branches require exact values: never average alpha-beta bounds.
      const a=outcomes.length>1?-Infinity:alpha;
      const b=outcomes.length>1?Infinity:beta;
      const sameTurn=outcome.state.isLightTurn===state.isLightTurn && !!outcome.state.activeBonusTurnPieceId;
      // Count complete turns, not each action in a Virus/Monkey/Penguin sequence.
      // Bound extensions so reversible bonus chains cannot monopolize a search.
      const nextDepth=depth+(sameTurn && chain<8 ? 1 : 0);
      const line=qleft>0 ? this.quiescence(outcome.state,a,b,ply,qleft-1) : this.search(outcome.state,nextDepth,a,b,ply,sameTurn?chain+1:0);
      score+=outcome.probability*line.score;
      if(outcome.probability>largest) {largest=outcome.probability;representative=line.pv;}
    }
    return {score,pv:representative};
  }

  private search(state:GameState,depth:number,alpha:number,beta:number,ply:number,chain=0):Line {
    this.checkTime();this.nodes++;
    const terminal=terminalScore(state,this.rootLight,ply);
    if(terminal!==undefined) return {score:terminal,pv:[]};
    if(depth<=0) return this.quiescence(state,alpha,beta,ply,4);
    const key=`${ply}|${chain}|${stateKey(state)}`;
    const originalAlpha=alpha,originalBeta=beta;
    const cached=this.table.get(key);
    if(cached && cached.depth>=depth) {
      this.ttHits++;
      if(cached.bound==='exact') return {score:cached.score,pv:cached.pv};
      if(cached.bound==='lower') alpha=Math.max(alpha,cached.score);
      else beta=Math.min(beta,cached.score);
      if(alpha>=beta) return {score:cached.score,pv:cached.pv};
    }
    const moves=this.rules.legalMoves(state);
    if(!moves.length) return {score:0,pv:[]};
    const maximizing=state.isLightTurn===this.rootLight;
    let best:Line={score:maximizing?-Infinity:Infinity,pv:[]};
    for(const move of this.order(state,moves,cached?.best,ply)) {
      const line=this.searchMove(state,move,depth-1,alpha,beta,ply+1,0,chain);
      if(!line) continue;
      if(maximizing?line.score>best.score:line.score<best.score) best={score:line.score,pv:[move,...line.pv]};
      if(maximizing) alpha=Math.max(alpha,line.score);else beta=Math.min(beta,line.score);
      if(alpha>=beta) {
        this.cutoffs++;
        const mk=moveKey(move);
        this.killers.set(ply,[mk,...(this.killers.get(ply)||[]).filter(k=>k!==mk)].slice(0,2));
        this.history.set(mk,(this.history.get(mk)||0)+depth*depth);
        break;
      }
    }
    if(!best.pv.length) return {score:this.staticScore(state),pv:[]};
    const bound:Bound=best.score<=originalAlpha?'upper':best.score>=originalBeta?'lower':'exact';
    if(this.table.size<30000) this.table.set(key,{depth,score:best.score,bound,best:moveKey(best.pv[0]),pv:best.pv});
    return best;
  }

  private quiescence(state:GameState,alpha:number,beta:number,ply:number,left:number):Line {
    this.checkTime();this.qnodes++;
    const terminal=terminalScore(state,this.rootLight,ply);
    if(terminal!==undefined) return {score:terminal,pv:[]};
    const standing=this.staticScore(state);
    if(left<=0) return {score:standing,pv:[]};
    const maximizing=state.isLightTurn===this.rootLight;
    const moves=this.rules.legalMoves(state);
    if(!moves.length) return {score:0,pv:[]};
    // A king capture/race threat means passing isn't an option: search all evasions.
    const opponent=copyState(state);
    opponent.isLightTurn=!state.isLightTurn;opponent.activeBonusTurnPieceId=null;opponent.skipTurnChange=false;
    const threatened=!state.activeBonusTurnPieceId && this.rules.legalMoves(opponent).some(m=>this.isKingTactic(opponent,m));
    const forced=!!state.activeBonusTurnPieceId || threatened;
    let best:Line={score:forced?(maximizing?-Infinity:Infinity):standing,pv:[]};
    if(!forced) {
      if(maximizing) {if(standing>=beta) return best;alpha=Math.max(alpha,standing);}
      else {if(standing<=alpha) return best;beta=Math.min(beta,standing);}
    }
    const candidates=forced?moves:moves.filter(m=>this.isTactical(state,m));
    for(const move of this.order(state,candidates,undefined,ply)) {
      const line=this.searchMove(state,move,0,alpha,beta,ply+1,left);
      if(!line) continue;
      if(maximizing?line.score>best.score:line.score<best.score) best={score:line.score,pv:[move,...line.pv]};
      if(maximizing) alpha=Math.max(alpha,line.score);else beta=Math.min(beta,line.score);
      if(alpha>=beta) {this.cutoffs++;break;}
    }
    return Number.isFinite(best.score)?best:{score:standing,pv:[]};
  }

  private isKingTactic(state:GameState,info:MoveInfo):boolean {
    const p=state.board[info.fromPosition.row][info.fromPosition.col].Pieces.find(p=>p.id===info.move.pieceId);
    return !!(info.move.toPiece?.isKing && info.move.toPiece.isLight!==state.isLightTurn) ||
      !!(p?.isKing && info.move.position.row===(p.isLight?7:0)) ||
      info.move.sideEffects.some(se=>se.effect.type===EffectType.capture && se.piecesToEffect.some(p=>p.isKing));
  }
  private isTactical(state:GameState,info:MoveInfo):boolean {
    return this.isKingTactic(state,info) || info.move.moveType===MoveType.trample ||
      (!!info.move.toPiece && info.move.toPiece.isLight!==state.isLightTurn) ||
      info.move.sideEffects.some(se=>[EffectType.capture,EffectType.bonusTurn,EffectType.entangle].includes(se.effect.type));
  }
  private order(state:GameState,moves:MoveInfo[],preferred:string|undefined,ply:number):MoveInfo[] {
    const score=(m:MoveInfo):number=>{
      const key=moveKey(m);
      if(key===preferred) return 10000000;
      const p=state.board[m.fromPosition.row][m.fromPosition.col].Pieces.find(p=>p.id===m.move.pieceId);
      let value=this.isKingTactic(state,m)?1000000:0;
      if(m.move.toPiece && m.move.toPiece.isLight!==state.isLightTurn) value+=10000+PIECE_VALUES[m.move.toPiece.type]*10-(p?PIECE_VALUES[p.type]:0);
      if(m.move.sideEffects.some(se=>se.effect.type===EffectType.bonusTurn)) value+=3000;
      if((this.killers.get(ply)||[]).includes(key)) value+=5000;
      value+=Math.min(2000,this.history.get(key)||0);
      value+=(state.isLightTurn?1:-1)*(m.move.position.row-m.fromPosition.row)*(p?.isKing?20:3);
      return value;
    };
    return moves.map(m=>({m,s:score(m)})).sort((a,b)=>b.s-a.s).map(x=>x.m);
  }
}
